import React, { useState } from "react";
import { CustomButton } from "./CustomButton";
import { HiOutlineTrash, HiPlus, HiX } from "react-icons/hi";
import { ModalShell } from "./ModalShell";
import {
  MdAttachFile,
  MdTextFields,
  MdNumbers,
  MdToggleOn,
  MdCalendarToday,
  MdDataObject,
  MdInfo,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useOpenPrivateFile } from "../../hooks/cdn";
import useAuth from "../../hooks/useAuth";
import NoTranslate from "./NoTranslate";
import { Copy } from "./Copy";
import { originalFileName } from "../../utils/storedFileName";
import { SpaceEmptyState } from "./SpaceEmptyState";

interface KeyValueTableProps {
  fields: Record<string, any>;
  onEditField: (fieldKey: string) => void;
  onDeleteField: (fieldKey: string) => void;
  onCreateField: () => void;
  maxLimit?: number;
  isLargeBusinessTier?: boolean;
  isReadOnlyFolder?: boolean;
  readOnlyTooltip?: string;
  onUpgradeClick?: () => void;
}

export const KeyValueTable: React.FC<KeyValueTableProps> = ({
  fields,
  onEditField,
  onDeleteField,
  onCreateField,
  maxLimit = 10,
  isLargeBusinessTier = false,
  isReadOnlyFolder = false,
  readOnlyTooltip = "",
  onUpgradeClick,
}) => {
  const { t } = useTranslation();
  const fieldKeys = Object.keys(fields);
  const canAddMore = isLargeBusinessTier || fieldKeys.length < maxLimit;
  const { user, customer_identification } = useAuth();

  const { open: openFile, isOpening: isGetting } = useOpenPrivateFile();

  const [viewValue, setViewValue] = useState<{
    key: string;
    value: string;
  } | null>(null);

  const getValuePreview = (value: any): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "string") return value || "-";
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean")
      return t(`dashboard.UserDetailsSpace.preview.${value ? "yes" : "no"}`);
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return t("dashboard.UserDetailsSpace.preview.items", {
          count: value.length,
        });
      }
      return t("dashboard.UserDetailsSpace.preview.structuredData");
    }
    return String(value);
  };

  const getValueType = (value: any): string => {
    if (value === null || value === undefined) return "empty";
    if (typeof value === "string") return "text";
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "object") {
      if (Array.isArray(value)) return "array";
      return "object";
    }
    return "unknown";
  };

  const getTypeStyle = (
    valueType: string
  ): {
    bg: string;
    border: string;
    iconColor: string;
    Icon: React.ComponentType<any>;
  } => {
    switch (valueType) {
      case "text":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          iconColor: "text-blue-600",
          Icon: MdTextFields,
        };
      case "number":
        return {
          bg: "bg-green-50",
          border: "border-green-200",
          iconColor: "text-green-600",
          Icon: MdNumbers,
        };
      case "boolean":
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          iconColor: "text-gray-600",
          Icon: MdToggleOn,
        };
      case "date":
        return {
          bg: "bg-blue-100",
          border: "border-blue-300",
          iconColor: "text-blue-800",
          Icon: MdCalendarToday,
        };
      case "file":
        return {
          bg: "bg-gray-100",
          border: "border-gray-300",
          iconColor: "text-gray-800",
          Icon: MdAttachFile,
        };
      case "object":
      case "array":
        return {
          bg: "bg-amber-50",
          border: "border-amber-200",
          iconColor: "text-amber-600",
          Icon: MdDataObject,
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          iconColor: "text-gray-600",
          Icon: MdTextFields,
        };
    }
  };

  const handleViewFile = (fileName: string) =>
    openFile({
      user_identification: customer_identification,
      entity_identification: user.entity_identification,
      file_name: fileName,
    });

  return (
    <>
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              {t("dashboard.UserDetailsSpace.fields")}
            </h3>
            {!isLargeBusinessTier && !isReadOnlyFolder && (
              <p className="text-sm text-gray-600 mt-1">
                {t("dashboard.UserDetailsSpace.fieldsLimit", {
                  count: fieldKeys.length,
                  max: maxLimit,
                })}
              </p>
            )}
          </div>
          {!isReadOnlyFolder && (
            <CustomButton
              variant={canAddMore ? "primary" : "gray"}
              onClick={canAddMore ? onCreateField : onUpgradeClick}
              icon={<HiPlus className="w-4 h-4" />}
              tooltip={
                !canAddMore
                  ? (t("dashboard.UserDetailsSpace.upgradeForMore") as string)
                  : (t("dashboard.UserDetailsSpace.addField") as string)
              }
              showUpgrade={!canAddMore}
              className="px-2 sm:px-4"
              title={
                <span className="hidden sm:inline">
                  {t("dashboard.UserDetailsSpace.addField")}
                </span>
              }
            />
          )}
        </div>

        {fieldKeys.length === 0 ? (
          <SpaceEmptyState
            title={t("dashboard.UserDetailsSpace.noFieldsYet")}
            subtitle={t("dashboard.UserDetailsSpace.createFirstField")}
            actionLabel={
              isReadOnlyFolder
                ? undefined
                : (t("dashboard.UserDetailsSpace.addField") as string)
            }
            onAction={isReadOnlyFolder ? undefined : onCreateField}
          />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="hidden sm:table-cell px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10"></th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/4">
                    {t("dashboard.UserDetailsSpace.key")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("dashboard.UserDetailsSpace.value")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-16 sm:w-32">
                    {t("dashboard.UserDetailsSpace.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {fieldKeys.map((key) => {
                  const value = fields[key];
                  const valuePreview = getValuePreview(value);
                  const valueType = getValueType(value);
                  const typeStyle = getTypeStyle(valueType);
                  const TypeIcon = typeStyle.Icon;

                  return (
                    <tr
                      key={key}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => !isReadOnlyFolder && onEditField(key)}
                    >
                      <td className="hidden sm:table-cell px-2 py-3 w-10">
                        <div className="flex items-center justify-center">
                          <TypeIcon
                            className={`w-4 h-4 ${typeStyle.iconColor} flex-shrink-0`}
                          />
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-3 w-1/4">
                        <div className="flex items-center min-w-0">
                          <span
                            className="text-xs font-medium text-gray-900 truncate block"
                            title={key}
                          >
                            <NoTranslate>{key}</NoTranslate>
                          </span>
                        </div>
                      </td>
                      <td
                        className="px-2 sm:px-3 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {valueType === "object" && !!value?.extension ? (
                            <button
                              onClick={() => handleViewFile(value.file_name)}
                              disabled={isGetting}
                              className={`flex-1 min-w-0 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed truncate text-xs font-medium`}
                            >
                              <MdAttachFile
                                className={`text-lg text-gray-700`}
                              />
                              <NoTranslate>
                                {value.file_name
                                  ? originalFileName(value.file_name)
                                  : valuePreview}
                              </NoTranslate>
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                setViewValue({
                                  key,
                                  value:
                                    valueType === "object" ||
                                    valueType === "array"
                                      ? JSON.stringify(value, null, 2)
                                      : valueType === "boolean"
                                      ? valuePreview
                                      : String(value ?? ""),
                                })
                              }
                              className="flex-1 min-w-0 text-left text-xs text-gray-600 truncate block hover:text-blue-600 transition-colors"
                              title={
                                t(
                                  "dashboard.UserDetailsSpace.viewFullValue"
                                ) as string
                              }
                            >
                              <NoTranslate>{valuePreview}</NoTranslate>
                            </button>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-2 py-3 text-right w-16 sm:w-32"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {!(valueType === "object" && !!value?.extension) && (
                            <Copy
                              target={
                                valueType === "object" || valueType === "array"
                                  ? JSON.stringify(value, null, 2)
                                  : valueType === "boolean"
                                  ? String(value)
                                  : valuePreview
                              }
                              iconOnly
                              title={
                                t("dashboard.UserDetailsSpace.copy") as string
                              }
                            />
                          )}
                          {!isReadOnlyFolder && (
                            <CustomButton
                              variant="none"
                              size="icon"
                              className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteField(key);
                              }}
                              tooltip={t("common.delete") as string}
                              icon={<HiOutlineTrash size={18} />}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {isReadOnlyFolder && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <MdInfo className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-700">{readOnlyTooltip}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalShell
        show={!!viewValue}
        onClose={() => setViewValue(null)}
        size="2xl"
        title={
          <span className="flex min-w-0 items-center gap-2">
            <MdTextFields className="h-5 w-5 shrink-0 text-blue-600" />
            <span className="truncate">
              {t("dashboard.UserDetailsSpace.dataPreview")}
            </span>
          </span>
        }
        footer={
          <CustomButton
            variant="primary"
            onClick={() => setViewValue(null)}
            icon={<HiX className="w-4 h-4" />}
            title={t("common.close")}
          />
        }
      >
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
              {t("dashboard.UserDetailsSpace.key")}
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 break-words">
              <NoTranslate>{viewValue?.key ?? ""}</NoTranslate>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
              {t("dashboard.UserDetailsSpace.value")}
            </div>
            <div className="max-h-[50vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
              <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 font-sans">
                <NoTranslate>{viewValue?.value ?? ""}</NoTranslate>
              </pre>
            </div>
          </div>
        </div>
      </ModalShell>
    </>
  );
};

export default KeyValueTable;
